import { computed, toValue } from 'vue'

import { openExternalUrl } from '@/utils/external-link'
import { beaconClickout } from '@/utils/analytics-beacons'

/**
 * Resolves which URL a bookmark's "open source" action should use, and opens
 * it the way the current platform wants (in-app browser on native).
 *
 * Only the collection owner gets the raw destination on public pages; everyone
 * else — members included, since stats went collection-wide — goes through the
 * clickout-counting redirect (`outUrl`, a 302 on our API that preserves
 * affiliate links). Keeping that rule here means it lives in one place — it
 * was previously duplicated across four bookmark components plus the public
 * detail view.
 *
 * @param {Object|import('vue').Ref} bookmark  the bookmark (ref, getter or plain)
 * @param {Object} options
 * @param {boolean|import('vue').Ref|Function} options.directUrl
 *        true to use the raw destination (in-app surfaces, or the owner on a
 *        public page), false to route through the counting redirect
 * @returns {{ sourceUrl: import('vue').ComputedRef<string|null>, openSource: Function }}
 */
export function useBookmarkSourceUrl(bookmark, { directUrl }) {
  const sourceUrl = computed(() => {
    const item = toValue(bookmark)
    if (!item) return null
    // Both getters share the same guard, so outUrl is null exactly when
    // brandedUrl is — no fallback needed between them.
    return toValue(directUrl) ? item.brandedUrl : item.outUrl
  })

  function openSource() {
    if (!sourceUrl.value) return
    openExternalUrl(sourceUrl.value)
  }

  return { sourceUrl, openSource }
}

/**
 * Same thing for the bookmark list/card/tile/headline components, which all
 * share one `{ bookmark, publicMode, membership, userId }` props contract.
 * In-app links are always direct — those clicks are counted with a beacon
 * instead of the redirect, since the app knows who is clicking and the server
 * can drop the owner's own clicks and dedup the rest.
 *
 * @param {Object} props the component's props object
 */
export function useBookmarkItemSourceUrl(props) {
  const { sourceUrl, openSource: open } = useBookmarkSourceUrl(() => props.bookmark, {
    directUrl: () => !props.publicMode || !!props.membership?.isOwner,
  })

  function openSource() {
    if (!props.publicMode) beaconClickout(props.bookmark, props.userId)
    open()
  }

  return { sourceUrl, openSource }
}
